import type { RefreshTokenResponse } from "@microbeta/common-types";
import { httpClient } from "../../../shared/utils/httpClientInstance";

export interface SignInResponse {
  kind: string;
  idToken: string;
  displayName: string;
  email: string;
  refreshToken: string;
  expiresIn: string;
  localId: string;
  emailVerified?: boolean;
}

export interface SignUpResponse {
  kind: string;
  idToken: string;
  displayName: string;
  email: string;
  refreshToken: string;
  expiresIn: string;
  localId: string;
}

// Error types for better error handling
export class AuthServiceError extends Error {
  constructor(
    message: string,
    public code?: string
  ) {
    super(message);
    this.name = "AuthServiceError";
  }
}

export const refreshToken = async (
  refreshToken: string
): Promise<RefreshTokenResponse> => {
  const endpoint = `${import.meta.env.VITE_AUTH_BASE_URL}/auth/refresh`;
  try {
    const response = await httpClient.post<RefreshTokenResponse>(endpoint, {
      refreshToken,
    });
    return response;
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new AuthServiceError(error.message, "REFRESH_TOKEN_FAILED");
    }
    throw new AuthServiceError(
      "Failed to refresh authentication token",
      "REFRESH_TOKEN_FAILED"
    );
  }
};

export const signIn = async (
  email: string,
  password: string,
  recaptchaToken?: string
): Promise<SignInResponse> => {
  const endpoint = `${import.meta.env.VITE_AUTH_BASE_URL}/auth/email/signin`;
  try {
    return await httpClient.post<SignInResponse>(endpoint, {
      email,
      password,
      recaptchaToken,
    });
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new AuthServiceError(error.message, "SIGN_IN_FAILED");
    }
    throw new AuthServiceError("Authentication failed", "SIGN_IN_FAILED");
  }
};

export const verifyOneTapCredential = async (
  credential: string
): Promise<SignInResponse> => {
  const endpoint = `${import.meta.env.VITE_AUTH_BASE_URL}/auth/google/onetap`;
  try {
    return await httpClient.post<SignInResponse>(endpoint, { credential });
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new AuthServiceError(error.message, "ONE_TAP_SIGN_IN_FAILED");
    }
    throw new AuthServiceError(
      "Google One Tap Authentication failed",
      "ONE_TAP_SIGN_IN_FAILED"
    );
  }
};

export const signUp = async (
  email: string,
  password: string,
  fullName: string,
  recaptchaToken?: string
): Promise<SignUpResponse> => {
  const endpoint = `${import.meta.env.VITE_AUTH_BASE_URL}/auth/email/signup`;
  try {
    return await httpClient.post<SignUpResponse>(endpoint, {
      email,
      password,
      fullName,
      recaptchaToken,
    });
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new AuthServiceError(error.message, "SIGN_UP_FAILED");
    }
    throw new AuthServiceError("Account creation failed", "SIGN_UP_FAILED");
  }
};

export const requestPasswordReset = async (
  email: string,
  recaptchaToken?: string
): Promise<void> => {
  const endpoint = `${
    import.meta.env.VITE_AUTH_BASE_URL
  }/auth/email/password-reset/send`;
  try {
    await httpClient.post<void>(endpoint, { email, recaptchaToken });
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new AuthServiceError(
        error.message,
        "PASSWORD_RESET_REQUEST_FAILED"
      );
    }
    throw new AuthServiceError(
      "Password reset request failed",
      "PASSWORD_RESET_REQUEST_FAILED"
    );
  }
};

export const resetPassword = async (
  oobCode: string,
  newPassword: string
): Promise<void> => {
  const endpoint = `${
    import.meta.env.VITE_AUTH_BASE_URL
  }/auth/email/password-reset/confirm`;
  try {
    await httpClient.post<void>(endpoint, { oobCode, newPassword });
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new AuthServiceError(error.message, "PASSWORD_RESET_FAILED");
    }
    throw new AuthServiceError(
      "Password reset failed",
      "PASSWORD_RESET_FAILED"
    );
  }
};

export const deleteAccount = async (): Promise<void> => {
  const endpoint = `${import.meta.env.VITE_AUTH_BASE_URL}/users/account`;
  try {
    await httpClient.del<void>(endpoint);
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new AuthServiceError(error.message, "DELETE_ACCOUNT_FAILED");
    }
    throw new AuthServiceError(
      "Account deletion failed",
      "DELETE_ACCOUNT_FAILED"
    );
  }
};
